// Patient context inside the appointment editor: a compact patient summary
// card (or a "New patient" flag when the appointment has no linked patient
// record) plus a browseable notes history. Note content from Plato is rich
// Froala HTML — it is whitelist-sanitized (see lib/safe-html.js) and rendered
// formatted; plain-text notes render pre-wrapped.

import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import {
  AlertTriangle,
  ChevronDown,
  FileText,
  Loader2,
  Mail,
  Phone,
  UserPlus,
  UserX,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { isDirectEntryEvent } from "@/lib/calendars";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { fetchAdminUser, fetchAdminUserNotes } from "@/lib/api/admin";
import {
  contactEmailList,
  contactPhoneList,
  formatAdminUserDisplayName,
  formatAdminUserDobDisplay,
  formatAdminUserSex,
  patientMetaField,
  pick,
} from "@/lib/admin-user-display";
import { looksLikeHtml, sanitizeNoteHtml } from "@/lib/safe-html";
import { NIL_PATIENT_ID } from "../calendar-utils";
import { FieldLabel } from "./form-fields";

const NOTES_PAGE_SIZE = 10;

function realPatientId(event) {
  const pid = String(event?.patientId || "").trim();
  return pid && pid !== NIL_PATIENT_ID ? pid : "";
}

function NewPatientFlag() {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
      <UserPlus className="mt-0.5 size-4 shrink-0 text-amber-500" aria-hidden />
      <div className="min-w-0 text-sm">
        <p className="font-semibold text-amber-600 dark:text-amber-400">New patient</p>
        <p className="text-xs text-muted-foreground">
          No patient record is linked to this appointment yet, so there is no
          profile or notes history to show.
        </p>
      </div>
    </div>
  );
}

// A direct calendar entry (standalone admin calendar) is not a visit: no patient is a
// perfectly normal state, so it gets a neutral line instead of the amber new-patient flag.
function NoPatientLine() {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-border/70 bg-muted/20 p-3">
      <UserX className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 text-sm">
        <p className="font-medium text-foreground">No patient attached</p>
        <p className="text-xs text-muted-foreground">
          This calendar entry isn&apos;t linked to a patient record.
        </p>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  if (!children) return null;
  return (
    <div className="flex min-w-0 items-baseline gap-1.5 text-xs">
      {Icon ? <Icon className="size-3 shrink-0 self-center text-muted-foreground/70" aria-hidden /> : null}
      {label ? (
        <span className="shrink-0 font-semibold uppercase tracking-wide text-[0.625rem] text-muted-foreground">
          {label}
        </span>
      ) : null}
      <span className="min-w-0 truncate text-foreground">{children}</span>
    </div>
  );
}

function PatientSummary({ user, loading, error }) {
  if (loading) {
    return (
      <div className="space-y-2 rounded-lg border border-border/70 bg-muted/20 p-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-56" />
        <Skeleton className="h-3 w-48" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
        {error}
      </div>
    );
  }
  if (!user) return null;

  const name = formatAdminUserDisplayName(user) || "Unnamed patient";
  const initial = name.trim()[0]?.toUpperCase() || "?";
  const dob = formatAdminUserDobDisplay(user);
  const sex = formatAdminUserSex(user);
  const phones = contactPhoneList(user.contacts)
    .map((p) => pick(p, "e164", "E164"))
    .filter(Boolean);
  const email = contactEmailList(user.contacts)
    .map((e) => pick(e, "email", "Email"))
    .filter(Boolean)[0];
  const pm = user.patientMeta || {};
  const nric = patientMetaField(pm, "nric", "NRIC");
  const allergies = patientMetaField(pm, "allergies");
  const alerts = patientMetaField(pm, "alerts");
  const tags = Array.isArray(pm.tags) ? pm.tags.filter(Boolean) : [];

  return (
    <div className="space-y-2.5 rounded-lg border border-border/70 bg-muted/20 p-3">
      <div className="flex items-center gap-2.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">
          {initial}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">{name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {[dob !== "—" ? dob : "", sex, nric].filter(Boolean).join(" · ") || "No profile details"}
          </p>
        </div>
      </div>

      <div className="grid gap-1">
        {phones.length > 0 ? (
          <InfoRow icon={Phone}>{phones.join(" · ")}</InfoRow>
        ) : null}
        {email ? <InfoRow icon={Mail}>{email}</InfoRow> : null}
      </div>

      {allergies || alerts ? (
        <div className="space-y-1">
          {allergies ? (
            <div className="flex items-start gap-1.5 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
              <span className="min-w-0">
                <span className="font-semibold">Allergies:</span> {allergies}
              </span>
            </div>
          ) : null}
          {alerts ? (
            <div className="flex items-start gap-1.5 rounded-md bg-rose-500/10 px-2 py-1 text-xs text-rose-700 dark:text-rose-400">
              <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
              <span className="min-w-0">
                <span className="font-semibold">Alerts:</span> {alerts}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="px-1.5 py-0 text-[0.625rem]">
              {tag}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function NoteContent({ content }) {
  const html = useMemo(
    () => (looksLikeHtml(content) ? sanitizeNoteHtml(content) : null),
    [content],
  );
  if (html != null) {
    return (
      <ScrollArea
        orientation="both"
        viewportClassName="max-h-[26rem]"
        className="rounded-md border border-border/60 bg-background"
      >
        <div
          // Contained typography for sanitized Froala HTML: images shrink to
          // fit, tables get borders + horizontal scroll, generous line-height.
          className={cn(
            "note-html p-3 text-sm leading-relaxed text-foreground",
            "[&_img]:my-1 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded",
            "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse",
            "[&_td]:border [&_td]:border-border/60 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border/60 [&_th]:px-2 [&_th]:py-1",
            "[&_p]:my-1 [&_div]:min-h-[0.5rem] [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-bold",
            "[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5",
            "[&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
            "[&_hr]:my-2 [&_hr]:border-border/60",
          )}
          // eslint-disable-next-line react/no-danger -- whitelist-sanitized in lib/safe-html.js
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </ScrollArea>
    );
  }
  return (
    <ScrollArea
      viewportClassName="max-h-[26rem] whitespace-pre-wrap p-3 text-sm leading-relaxed text-foreground"
      className="rounded-md border border-border/60 bg-background"
    >
      {String(content || "").trim() || "(empty note)"}
    </ScrollArea>
  );
}

function noteDateLabel(note) {
  const raw = note?.updatedAt || note?.createdAt;
  if (!raw) return "";
  const dt = DateTime.fromISO(String(raw));
  return dt.isValid ? dt.toFormat("d LLL yyyy") : "";
}

function NoteRow({ note, expanded, onToggle }) {
  const title = String(note?.title || "").trim() || "Untitled note";
  const folder = String(note?.folderName || "").trim();
  const kind = String(note?.sourceType || "").trim();
  return (
    <li className="rounded-lg border border-border/70 bg-muted/10">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-muted/40"
        aria-expanded={expanded}
      >
        <FileText className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground">{title}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {[noteDateLabel(note), folder && folder !== title ? folder : "", kind === "letter" ? "Letter" : ""]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none",
            expanded && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {expanded ? (
        <div className="px-3 pb-3">
          <NoteContent content={note?.content} />
        </div>
      ) : null}
    </li>
  );
}

/**
 * Patient summary + browseable notes history for the appointment editor.
 * Fetches lazily when the sheet opens; renders a "New patient" flag when the
 * appointment has no linked patient record.
 */
export function AppointmentPatientPanel({ event, open }) {
  const patientId = realPatientId(event);

  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState("");
  const [userMissing, setUserMissing] = useState(false);

  const [notes, setNotes] = useState([]);
  const [notesCount, setNotesCount] = useState(0);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const notesRequestRef = useRef(0);

  useEffect(() => {
    if (!open || !patientId) {
      setUser(null);
      setUserError("");
      setUserMissing(false);
      setNotes([]);
      setNotesCount(0);
      setNotesError("");
      setNotesOpen(false);
      setExpandedNoteId(null);
      return undefined;
    }
    let cancelled = false;
    const requestId = notesRequestRef.current + 1;
    notesRequestRef.current = requestId;

    setUserLoading(true);
    setUserError("");
    setUserMissing(false);
    fetchAdminUser({ userId: patientId })
      .then((doc) => {
        if (!cancelled) setUser(doc);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.status === 404) {
          // The appointment points at a patient record that doesn't exist
          // (e.g. not synced yet) — present it as a new patient, not an error.
          setUserMissing(true);
        } else {
          setUserError(err?.message || "Could not load patient.");
        }
      })
      .finally(() => {
        if (!cancelled) setUserLoading(false);
      });

    setNotes([]);
    setNotesCount(0);
    setExpandedNoteId(null);
    setNotesLoading(true);
    setNotesError("");
    fetchAdminUserNotes({ userId: patientId, limit: NOTES_PAGE_SIZE })
      .then((res) => {
        if (cancelled || notesRequestRef.current !== requestId) return;
        setNotes(res.items);
        setNotesCount(res.count);
      })
      .catch((err) => {
        if (!cancelled && err?.status !== 404) {
          setNotesError(err?.message || "Could not load notes.");
        }
      })
      .finally(() => {
        if (!cancelled) setNotesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, patientId]);

  const loadMoreNotes = async () => {
    if (notesLoading) return;
    const requestId = notesRequestRef.current + 1;
    notesRequestRef.current = requestId;
    setNotesLoading(true);
    setNotesError("");
    try {
      const res = await fetchAdminUserNotes({
        userId: patientId,
        limit: NOTES_PAGE_SIZE,
        skip: notes.length,
      });
      if (notesRequestRef.current !== requestId) return;
      setNotes((current) => [...current, ...res.items]);
      setNotesCount(res.count);
    } catch (err) {
      setNotesError(err?.message || "Could not load more notes.");
    } finally {
      setNotesLoading(false);
    }
  };

  if (!patientId || userMissing) {
    return (
      <div className="space-y-1.5">
        <FieldLabel>Patient</FieldLabel>
        {!patientId && isDirectEntryEvent(event) ? <NoPatientLine /> : <NewPatientFlag />}
      </div>
    );
  }

  const remaining = Math.max(notesCount - notes.length, 0);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <FieldLabel>Patient</FieldLabel>
        <PatientSummary user={user} loading={userLoading} error={userError} />
      </div>

      <div className="space-y-1.5">
        <button
          type="button"
          onClick={() => setNotesOpen((current) => !current)}
          aria-expanded={notesOpen}
          className="flex w-full items-center gap-2 rounded-md py-0.5 text-left transition-colors hover:text-foreground"
        >
          <FieldLabel>Notes history</FieldLabel>
          {notesLoading && notes.length === 0 ? (
            <Loader2 className="size-3 animate-spin text-muted-foreground" aria-hidden />
          ) : (
            <Badge variant="secondary" className="px-1.5 py-0 text-[0.625rem] tabular-nums">
              {notesCount}
            </Badge>
          )}
          <ChevronDown
            className={cn(
              "ml-auto size-4 text-muted-foreground transition-transform motion-reduce:transition-none",
              notesOpen && "rotate-180",
            )}
            aria-hidden
          />
        </button>

        {notesOpen ? (
          <>
            {notesError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-xs text-destructive">
                {notesError}
              </div>
            ) : null}

            {notes.length === 0 && notesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
            ) : notes.length === 0 && !notesError ? (
              <p className="rounded-md border border-border/60 bg-muted/10 p-2.5 text-xs text-muted-foreground">
                No notes recorded for this patient.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {notes.map((note) => (
                  <NoteRow
                    key={note.id}
                    note={note}
                    expanded={expandedNoteId === note.id}
                    onToggle={() =>
                      setExpandedNoteId((current) => (current === note.id ? null : note.id))
                    }
                  />
                ))}
              </ul>
            )}

            {remaining > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={loadMoreNotes}
                disabled={notesLoading}
              >
                {notesLoading ? (
                  <Loader2 className="mr-2 size-3.5 animate-spin" aria-hidden />
                ) : null}
                Show older notes ({remaining} more)
              </Button>
            ) : null}
            {notes.length > 0 && notesLoading && remaining === 0 ? (
              <div className="flex justify-center py-1">
                <Spinner className="size-4" />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
